import React, { FC, MouseEventHandler, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FireIcon } from '@/atoms/Icons/FireIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { MusicIcon } from '@/atoms/Icons/MusicIcon'
import { CaptionXS, LabelSM } from '@/atoms/Text'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useEnhancedShowtimeContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import { convertUTCToLocalTime } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/utils'

interface ShowTimeLinkProps {
  showTime: TheatricalShowtime
  countryCode: string
  onClick: MouseEventHandler<HTMLButtonElement>
  isDisabled?: boolean
  shouldShowNewBadge?: boolean
}

export const ShowTimeLink: React.FC<ShowTimeLinkProps> = ({
  showTime,
  countryCode,
  onClick,
  isDisabled = false,
  shouldShowNewBadge = false,
}) => {
  const { query } = useSafeRouter()
  const { isShowtimeEnhanced } = useEnhancedShowtimeContext()
  const { locale } = useLocale()
  const [isLoading, setIsLoading] = useState(false)

  const hasPurchaseTicketsUrl = !!showTime?.ticketUrl
  const isSoldOut = showTime?.availableInventory === 0 && (showTime?.totalSeats ?? 0) > 0
  const hasShowTimePassed = showTime?.utcStartTime && new Date(showTime?.utcStartTime) < new Date()
  const shouldDisplayFlame = useMemo(
    () => isShowtimeEnhanced(showTime?.ticketingId ?? ''),
    [isShowtimeEnhanced, showTime?.ticketingId],
  )
  const startTime = convertUTCToLocalTime(showTime?.utcStartTime, showTime?.localTimeOffset, locale)
  const isNew = shouldShowNewBadge && showTime?.isNew

  const isLinkEnabled = useMemo(() => {
    if (query?.promo && countryCode === 'PH' && !hasShowTimePassed) return true
    return !isSoldOut && !hasShowTimePassed && hasPurchaseTicketsUrl && !isDisabled
  }, [hasPurchaseTicketsUrl, hasShowTimePassed, isDisabled, isSoldOut, query?.promo, countryCode])

  useEffect(() => {
    if (isLoading && isDisabled) setIsLoading(true)
    if (isLoading && !isDisabled) setIsLoading(false)
  }, [isDisabled, isLoading])

  const iconVariant: IconType | null = useMemo(() => {
    if (isSoldOut) return 'sold-out'
    if (shouldDisplayFlame) return 'flame'
    if (isNew) return 'new'
    return null
  }, [isSoldOut, shouldDisplayFlame, isNew])

  if (!startTime) return null

  return (
    <Button
      onClick={(e) => {
        onClick(e)
        setIsLoading(true)
      }}
      disabled={!isLinkEnabled}
      className={classNames(
        'relative h-10 w-full max-w-[5.4rem] flex gap-1 items-center shrink-0 p-0 md:max-w-[6.5rem]',
        !isLinkEnabled && 'pointer-events-none',
      )}
      variant="oxide-300"
      data-dd-action-name="Tickets Showtime Selected"
    >
      {isLoading && <LoadingSpinnerIcon color="core-gray-600" size={16} />}
      {showTime.releaseType === 'SING_ALONG' && <MusicIcon size={16} />}
      <LabelSM weight="semibold">{startTime}</LabelSM>
      {iconVariant && <ShowtimeIcon variant={iconVariant} />}
    </Button>
  )
}

type IconType = 'sold-out' | 'flame' | 'new'

interface showtimeIconProps {
  variant: IconType | null
}
const ShowtimeIcon: FC<showtimeIconProps> = ({ variant }) => {
  const { t } = useTranslate('tickets')

  if (variant === 'flame') {
    return (
      <div className="absolute -right-2 -top-3 rounded-full border-[1.5px] border-warm-01 bg-warm-01 p-1">
        <FireIcon color="white" size={12} />
      </div>
    )
  }

  if (variant === 'sold-out') {
    return (
      <div className="absolute -right-2 -top-3 shrink-0 rounded-full border-[1.5px] border-warm-01 bg-warm-01 px-1.5">
        <CaptionXS weight="bold" className="text-[0.5rem]" color="white">
          {t('soldOut', 'Sold Out')}
        </CaptionXS>
      </div>
    )
  }

  if (variant === 'new') {
    return (
      <div className="absolute -right-1.5 -top-2 shrink-0 rounded-full bg-info-600 px-2 pt-[1px]">
        <CaptionXS weight="bold" className="uppercase tracking-wide" color="white">
          {t('new', 'New')}
        </CaptionXS>
      </div>
    )
  }

  return null
}
